import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { FaEdit, FaTrash, FaReceipt, FaSyncAlt } from 'react-icons/fa'; // Added icons
import { Oval } from "react-loader-spinner"; // Used for a better loading state
import { StandardURL } from "../App";


// Helper function for currency formatting (improves UI readability)
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', // Assuming KES for demonstration
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const UpdateBills = () => {
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false); // New state for update button loading
    const [editBillId, setEditBillId] = useState(null);
    const [serviceFee, setServiceFee] = useState("");
    const [totalAmount, setTotalAmount] = useState("");

    useEffect(() => {
        fetchUserBills();
    }, []);

    const fetchUserBills = async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/bill/viewallbills`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setBills(response.data.response);
            toast.info(`Fetched ${response.data.response.length} bills.`);
        } catch (error) {
            toast.error("Failed to fetch bills");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (bill) => {
        // Clear previous edit state first
        setEditBillId(null); 
        // Set new state on next render cycle
        setTimeout(() => {
            setEditBillId(bill.bill_id);
            // Ensure inputs are strings for controlled component
            setServiceFee(String(bill.service_fee)); 
            setTotalAmount(String(bill.total_amount));
        }, 0);
    };

    const handleUpdate = async () => {
        if (!editBillId) {
            toast.error("No bill selected for update");
            return;
        }

        const token = localStorage.getItem("token");
        setIsUpdating(true); // Start loading

        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/admin/bill/updatebill/${editBillId}`,
                {
                    servicefee: serviceFee,
                    totalamount: totalAmount,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success(response.data.response);
            fetchUserBills(); // Refresh the bill list
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Failed to update bill.";
            toast.error(errorMessage);
        } finally {
            setEditBillId(null); // Hide the edit section after attempt
            setIsUpdating(false); // Stop loading
        }
    };

    const handleDelete = async (billId) => {
        if (!window.confirm(`Are you sure you want to delete bill ID ${billId}?`)) {
            return;
        }
        
        const token = localStorage.getItem("token");
        try {
            await axios.delete(
                `${StandardURL}/iNyumba/admin/bill/deleteonebill/${billId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success("Bill deleted successfully");
            fetchUserBills(); // Refresh the bill list after deletion
        } catch (error) {
            toast.error("Failed to delete bill");
        }
    };

    // Filter bills to only show those with payment_status 'PENDING'
    const pendingBills = bills.filter(
        (bill) => bill.payment_status === "PENDING"
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <Oval
                    height={50}
                    width={50}
                    color="#3b82f6"
                    secondaryColor="#93c5fd"
                    ariaLabel="loading-indicator"
                />
                <p className="ml-3 text-lg text-gray-700">Fetching bills...</p>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-screen-xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaReceipt className="text-blue-600 mr-3" /> PENDING Bills Management
                        </h1>
                        
                        <div className="flex justify-end mb-4">
                             <button
                                onClick={fetchUserBills}
                                className="flex items-center bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 text-sm"
                            >
                                <FaSyncAlt className="mr-2" /> Refresh Bills
                            </button>
                        </div>

                        {pendingBills.length === 0 ? (
                            <div className="p-10 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-xl">
                                <FaReceipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-xl font-medium">No Pending Bills Found</p>
                                <p>All bills are currently settled or none have been generated yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            {/* Adjusted columns for better readability and alignment */}
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rental/Prop ID</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Water Bill</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rent Bill</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Service Fee</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Trash Fee</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider text-red-600">Total Amount</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {pendingBills.map((bill, index) => (
                                            <React.Fragment key={bill.bill_id}>
                                                <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{bill.bill_id}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{bill.rental_id} / {bill.property_id}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{formatCurrency(bill.water_bill)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{formatCurrency(bill.rent_bill)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{formatCurrency(bill.service_fee)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">{formatCurrency(bill.trashbill)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-red-600">{formatCurrency(bill.total_amount)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                            {bill.payment_status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                        <button
                                                            onClick={() => handleEdit(bill)}
                                                            className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded-full hover:bg-blue-100 transition"
                                                            title="Edit Bill Amounts"
                                                        >
                                                            <FaEdit className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(bill.bill_id)}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition"
                                                            title="Delete Bill"
                                                        >
                                                            <FaTrash className="w-5 h-5" />
                                                        </button>
                                                    </td>
                                                </tr>

                                                {/* Inline Edit Form (Improved UI) */}
                                                {editBillId === bill.bill_id && (
                                                    <tr className="border-b bg-blue-50">
                                                        <td colSpan="9" className="py-4 px-6">
                                                            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 items-end">
                                                                <div className="flex-1">
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                        Service Fee
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={serviceFee}
                                                                        onChange={(e) => setServiceFee(e.target.value)}
                                                                        className="w-full px-4 py-2 border border-blue-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                                        Total Amount
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={totalAmount}
                                                                        onChange={(e) => setTotalAmount(e.target.value)}
                                                                        className="w-full px-4 py-2 border border-blue-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                                                    />
                                                                </div>
                                                                <button
                                                                    onClick={handleUpdate}
                                                                    disabled={isUpdating}
                                                                    className={`w-full md:w-auto inline-flex items-center justify-center font-bold py-2.5 px-6 rounded-lg shadow-md transition duration-200 text-sm 
                                                                        ${isUpdating
                                                                            ? "bg-green-300 cursor-not-allowed"
                                                                            : "bg-green-600 hover:bg-green-700 text-white"
                                                                        } disabled:opacity-70`}
                                                                >
                                                                    {isUpdating ? (
                                                                        <>
                                                                            <Oval height={18} width={18} color="white" secondaryColor="#ffffff" strokeWidth={5} />
                                                                            <span className="ml-2">Updating...</span>
                                                                        </>
                                                                    ) : (
                                                                        "Confirm Update"
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateBills;